// src/services/invoiceService.js
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  limit,
  serverTimestamp,
  updateDoc,
  doc,
  increment,
  getDoc,
} from "firebase/firestore";
import { createInvoice } from "./voucherService";
import { getAuth } from "firebase/auth";
import computeCommissionsForSave from "../helpers/commissions";
import {
  PRODUCT_MASTER_COLLECTION,
  PRODUCT_PART_NO_FIELDS,
} from "../config/productMasterFirestore";

async function findProductMasterRefByItemCode(code) {
  const c = String(code || "").trim();
  if (!c) return null;
  for (const field of PRODUCT_PART_NO_FIELDS) {
    const qy = query(
      collection(db, PRODUCT_MASTER_COLLECTION),
      where(field, "==", c),
      limit(1)
    );
    const snap = await getDocs(qy);
    if (!snap.empty) {
      return doc(db, PRODUCT_MASTER_COLLECTION, snap.docs[0].id);
    }
  }
  return null;
}

/** update stock: add purchased qty */
export const updateStockPlus = async (purchasedItems) => {
  for (const row of purchasedItems) {
    const qty = Number(row.qty || 0);
    const itemCode = String(row.code || row.partno || "").trim();
    if (!itemCode || !qty) continue;
    try {
      const ref = await findProductMasterRefByItemCode(itemCode);
      if (ref) {
        await updateDoc(ref, { stock: increment(qty) });
      } else {
        console.warn("⚠️ Item not found for StockPlus:", itemCode);
      }
    } catch (err) {
      console.error("❌ Error updating StockPlus for:", itemCode, err);
    }
  }
};

/** update stock: subtract sold qty */
export const updateStockMinus = async (items) => {
  for (const row of items) {
    const qty = Number(row.qty || 0);
    const itemCode = String(row.code || row.partno || "").trim();
    if (!itemCode || !qty) continue;
    try {
      const ref = await findProductMasterRefByItemCode(itemCode);
      if (ref) {
        await updateDoc(ref, { stock: increment(-qty) });
      } else {
        console.warn("⚠️ Item not found for StockMinus:", itemCode);
      }
    } catch (err) {
      console.error("❌ Error updating StockMinus for:", itemCode, err);
    }
  }
};

/** helper: resolve username (users/{uid} -> localStorage -> admin) */
const resolveUsernamePreferUsersDocThenLocal = async (user) => {
  try {
    if (user?.uid) {
      try {
        const uRef = doc(db, "users", user.uid);
        const uSnap = await getDoc(uRef);
        if (uSnap.exists()) {
          const d = uSnap.data() || {};
          if (d.username && String(d.username).trim() !== "") return d.username;
          if (d.displayName && String(d.displayName).trim() !== "")
            return d.displayName;
        }
      } catch (err) {
        console.warn("resolveUsername: failed to read users/{uid}", err);
      }
    }

    try {
      const saved = localStorage.getItem("user");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.username && String(parsed.username).trim() !== "")
          return parsed.username;
      }
    } catch (err) {
      console.warn("resolveUsername: failed to read localStorage user", err);
    }

    return "admin";
  } catch (err) {
    console.warn("resolveUsername unexpected error", err);
    return "admin";
  }
};

/**
 * Helper: mergeReturnedCommissions
 *
 * Robustly map commission results returned by computeCommissionsForSave (which may
 * reorder or slightly differ) back onto the current items array. Tries a key
 * based on code::qty::rate, then falls back to positional mapping, then to any
 * existing commissionAmount on the original row.
 */
const mergeReturnedCommissions = (currentItems = [], helperResult = {}) => {
  const returnedItemsRaw = Array.isArray(helperResult?.items)
    ? helperResult.items
    : Array.isArray(helperResult)
    ? helperResult
    : [];

  const getCommissionFrom = (obj) =>
    Number(
      obj?.commissionAmount ??
        obj?.commission_amount ??
        obj?.commission ??
        obj?.commissionAmt ??
        obj?.comm ??
        0
    ) || 0;

  // Build map keyed by code::qty::rate to account for reordering
  const retMap = new Map();
  returnedItemsRaw.forEach((ri) => {
    const code = (ri.code || ri.itemCode || ri.item || "").toString().trim().toLowerCase();
    const qty = Number(ri.qty ?? ri.quantity ?? ri.q ?? 0);
    const rate = Number(ri.rate ?? ri.unitPrice ?? ri.price ?? 0);
    const key = `${code}::${qty}::${rate}`;
    if (!retMap.has(key)) retMap.set(key, []);
    retMap.get(key).push(ri);
  });

  // fallback list (shallow copy) in case keyed lookup fails
  const fallback = [...returnedItemsRaw];

  return currentItems.map((orig, idx) => {
    const code = (orig.code || orig.itemCode || orig.sku || "").toString().trim().toLowerCase();
    const qty = Number(orig.qty || orig.quantity || 0);
    const rate = Number(orig.rate || orig.unitPrice || orig.price || 0);
    const key = `${code}::${qty}::${rate}`;

    let commissionAmount = 0;
    if (retMap.has(key) && retMap.get(key).length > 0) {
      const ri = retMap.get(key).shift();
      commissionAmount = getCommissionFrom(ri);
    } else if (fallback[idx]) {
      // positional fallback
      commissionAmount = getCommissionFrom(fallback[idx]);
    } else {
      commissionAmount = Number(orig.commissionAmount || 0);
    }

    return { ...orig, commissionAmount };
  });
};

/**
 * saveInvoice (named export)
 *
 * Accepts a payload containing items and metadata and creates invoice via createInvoice()
 * Ensures per-row commissionAmount is preserved and top-level commission metadata saved.
 */
export const saveInvoice = async ({
  items,
  invoiceDate,
  selectedParty,
  deliveryAddress,
  transactionClass,
  cashCredit,
  totals,
  roundOff,
  grandTotalRounded,
  type,
  payment,
  packingAndForwarding,
  isInterState,
  clientToken,
  partyName,
  invoiceDateStr,
  commission, // { total, employeePartyId }
  createdByNameOverride,
  /** When opened from Billing Find (import), keep same voucher number. */
  voucherNoOverride,
  importSource,
  importPeriodKey,
}) => {
  try {
    const allowed = [
      "inward",
      "outward",
      "creditnote",
      "debitnote",
      "materialrequest",
    ];
    const t = String(type || "").toLowerCase();
    if (!t || !allowed.includes(t)) {
      throw new Error(
        "Invoice type is required (inward, outward, creditnote, debitnote, materialrequest)"
      );
    }

    // -----------------------
    // AUTO-COMMISSION COMPUTE (use shared helper computeCommissionsForSave)
    // -----------------------
    let computedResult = null;
    try {
      const empId = commission?.employeePartyId || null;
      console.log("Debug: computing commissions via shared helper for empId:", empId);
      computedResult = await computeCommissionsForSave(items || [], empId);
      console.log("Debug: computeCommissionsForSave returned:", computedResult);
    } catch (e) {
      console.warn("Debug: computeCommissionsForSave failed, falling back to zero commissions", e);
      computedResult = { items: (items || []).map(it => ({ ...it, commissionAmount: Number(it.commissionAmount || 0) })), total: 0 };
    }

    // merge returned commissions robustly to keep original ordering/rows
    const itemsWithCommission = mergeReturnedCommissions(items || [], computedResult || { items: [], total: 0 });

    const computedCommission = { ...(commission || {}), total: Number(computedResult?.total || 0) || 0 };

    console.log("Debug: computedCommission total BEFORE save:", computedCommission);

    const auth = getAuth();
    const user = auth.currentUser;
    const createdBy = user?.uid || null;
    const createdByName =
      createdByNameOverride || (await resolveUsernamePreferUsersDocThenLocal(user));

    const payload = {
      date: invoiceDate ? new Date(invoiceDate).toISOString() : new Date().toISOString(),
      invoiceDateStr: invoiceDateStr || invoiceDate || new Date().toISOString().slice(0, 10),
      party: selectedParty?.partyName || partyName || "",
      partyDetails: selectedParty || {},
      deliveryAddress: {
        party: deliveryAddress?.party || deliveryAddress?.partyName || selectedParty?.partyName || "",
        address: deliveryAddress?.address || "",
        gstin: deliveryAddress?.gstin || selectedParty?.gstin || "",
        phone: deliveryAddress?.phone || selectedParty?.phone || "",
      },
      transactionClass: transactionClass || "",
      cashCredit: cashCredit || "CASH",
      // use computed items (with per-row commissionAmount)
      items: (itemsWithCommission || []).map((it) => ({
        code: it.code || "",
        desc: it.desc || "",
        qty: Number(it.qty) || 0,
        unit: String(it.unit || "").trim() || "Nos",
        rate: Number(it.rate) || 0,
        discount: Number(it.discount) || 0,
        gst: Number(it.gst ?? it.GST) || 18,
        mrp: Number(it.mrp) || 0,
        commissionAmount: Number(it.commissionAmount || 0),
      })),
      totals,
      roundOff,
      grandTotal:
        Number(grandTotalRounded) ||
        (Number(roundOff || 0) + Number(totals?.net || 0)),
      createdBy,
      createdByName,
      status: "new",
      created: serverTimestamp(),

      // extra fields
      payment: payment || { mode: "Cash", amountPaid: 0, details: {} },
      packingAndForwarding: Number(packingAndForwarding || 0),
      isInterState: Boolean(isInterState || false),
      clientToken: clientToken || null,
      // store computed commission object
      commission: computedCommission,
      importSource: importSource || null,
      importPeriodKey: importPeriodKey || null,
    };

    const voucherOverride = String(voucherNoOverride || "").trim();
    if (voucherOverride) {
      const existingQ = query(
        collection(db, "invoices"),
        where("voucherNo", "==", voucherOverride),
        where("type", "==", t),
        limit(1)
      );
      const existingSnap = await getDocs(existingQ);
      if (!existingSnap.empty) {
        const ref = existingSnap.docs[0].ref;
        await updateDoc(ref, {
          ...payload,
          voucherNo: voucherOverride,
          modified: serverTimestamp(),
          status: "modify",
        });
        return { id: ref.id, voucherNo: voucherOverride, updated: true };
      }
    }

    const { id, voucherNo } = await createInvoice(payload, t, {
      voucherNoOverride: voucherOverride || undefined,
    });

    // update stock based on invoice type
    if (t === "inward") {
      await updateStockPlus(items || []);
    } else if (t === "outward") {
      await updateStockMinus(items || []);
    }

    return { id, voucherNo };
  } catch (err) {
    console.error("Error saving invoice (service):", err);
    throw err;
  }
};
